/*
 * @providesModule HelpModal
 *
 * Reusable help modal (bottom sheet) with title and body text.
 * Used by SignInView, SignUpView, and other flows that need a simple help/info modal.
 * Use variant 'sign-in' or 'sign-up' to use built-in copy; otherwise pass title and body.
 */

import React, {useMemo} from 'react';
import {Text, View, ViewStyle} from 'react-native';
import {_m} from 'INTL';
import {ModalBottomSheet} from '@skillz/ui';
import {helpModalStyles} from './HelpModal.style';
import Label from 'Label';

const DEFAULT_CLOSE_LABEL = _m('Close');
const DEFAULT_FOOTER_COLOR = '#4890E8';
const DEFAULT_MAX_HEIGHT = 500;

export type HelpModalSignInTab = 'email' | 'phone';

function getSignInHelpCopy(signInTab: HelpModalSignInTab, hasBothSignInMethods: boolean) {
  const title =
    signInTab === 'email'
      ? _m('Sign in with your email or username.')
      : _m('Sign in with your phone number.');
  const bodyBeginning =
    signInTab === 'email'
      ? _m('Sign in to your Skillz account with your email or username.')
      : _m('Sign in to your Skillz account with your mobile phone number.');
  const bodyEnd =
    signInTab === 'email'
      ? _m(
          'If you did not sign up with your email, then you must sign in with your phone number by tapping "Phone".'
        )
      : _m(
          'If you have not provided your phone number, then you can still sign in with your email or username by tapping "Email / Username".'
        );
  const body = hasBothSignInMethods ? bodyBeginning + bodyEnd : bodyBeginning;
  return {title, body};
}

function getSignUpHelpCopy() {
  const title = _m('Enter your phone to protect your account.');
  const body =
    _m(
      'We require your phone to verify your identity, protect your account, and provide a safe and fun platform for everyone.'
    ) +
    _m(
      'Providing your phone also unlocks access to free rewards, pro tournaments, support, and more!\n\n'
    );
  return {title, body};
}

export interface HelpModalPropsBase {
  /** Whether the modal is visible */
  visible: boolean;
  /** Called when the modal should close (e.g. footer button or backdrop) */
  onClose: () => void;
  /** Footer button label. Default: "Close" */
  footerText?: string;
  /** Footer button color. Default: #4890E8 */
  footerColor?: string;
  /** Max height of the sheet. Default: 500 */
  maxHeight?: number;
  /** Optional extra styles for the footer wrapper (e.g. helpModalContentWrapper) */
  additionalFooterStyles?: ViewStyle;
  /** Optional testID for the modal container */
  testID?: string;
}

export interface HelpModalPropsWithVariant extends HelpModalPropsBase {
  variant: 'sign-in';
  signInTab: HelpModalSignInTab;
  hasBothSignInMethods: boolean;
  title?: never;
  body?: never;
}

export interface HelpModalPropsSignUp extends HelpModalPropsBase {
  variant: 'sign-up';
  title?: never;
  body?: never;
}

export interface HelpModalPropsCustom extends HelpModalPropsBase {
  variant?: undefined;
  /** Modal title (required when not using variant) */
  title: string;
  /** Modal body text (required when not using variant) */
  body: string;
}

export type HelpModalProps =
  | HelpModalPropsWithVariant
  | HelpModalPropsSignUp
  | HelpModalPropsCustom;

export default function HelpModal(props: HelpModalProps): React.ReactElement {
  const {
    visible,
    onClose,
    footerText = DEFAULT_CLOSE_LABEL,
    footerColor = DEFAULT_FOOTER_COLOR,
    maxHeight = DEFAULT_MAX_HEIGHT,
    additionalFooterStyles,
    testID,
  } = props;

  const {title, body} = useMemo(() => {
    if (props.variant === 'sign-in') {
      return getSignInHelpCopy(props.signInTab, props.hasBothSignInMethods);
    }
    if (props.variant === 'sign-up') {
      return getSignUpHelpCopy();
    }
    return {title: props.title, body: props.body};
  }, [props]);

  return (
    <View>
      <ModalBottomSheet
        additionalFooterStyles={additionalFooterStyles ?? helpModalStyles.contentWrapper}
        visible={visible}
        maxHeight={maxHeight}
        onRequestClose={onClose}
        footerText={footerText}
        footerOnPress={onClose}
        footerColor={footerColor}
        testID={testID}
      >
        <View style={helpModalStyles.container}>
          <Label style={helpModalStyles.title}>{title}</Label>
          <Label style={helpModalStyles.body}>{body}</Label>
        </View>
      </ModalBottomSheet>
    </View>
  );
}
